import API from "serviceshift-ui/api-client";

// Shapes from the API keys contract (Admin::V4::ApiKeysController): the
// custom integration apps a tenant creates and their keys, managed from the
// API access page. A key itself comes back only from create and roll, and
// only to the caller: it never goes into the store.
//
// Every request opts out of the global error flash (ignoreError): the page
// shows its own plain errors, inline where a form field is at fault.

export type ApiKeyStatus = "active" | "expired" | "revoked" | "disabled";

export interface ApiKeyCredential {
  id: string;
  token_prefix: string;
  scopes: string[];
  status: ApiKeyStatus;
  created_at: string;
  created_by: string | null;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
}

export interface ApiKey {
  id: string;
  name: string;
  status: "active" | "disabled";
  created_at: string;
  created_by: string | null;
  // Newest first.
  credentials: ApiKeyCredential[];
}

export interface ApiKeyScope {
  key: string;
  name: string;
  description: string;
}

export interface ApiKeysResponse {
  data: ApiKey[];
  meta: {
    // The account can list and revoke keys, but not create or roll them.
    read_only: boolean;
    scopes: ApiKeyScope[];
    expiry_days: number[];
    // The company's Zapier keys: not listed here, managed on the Zapier page.
    zapier_keys?: { count: number };
  };
}

export interface MintedApiKey {
  data: ApiKey;
  key: string;
  credential_id: string;
}

export interface ApiKeyParams {
  name: string;
  scopes: string[];
  expires_in_days: number | null;
}

export interface RollApiKeyParams {
  apiKeyId: string;
  credentialId: string;
  expires_in_days: number | null;
}

const BASE = "/admin/v4/api_keys";
const QUIET = { headers: { ignoreError: true } };

async function send<T>(path: string, data?: unknown): Promise<T> {
  const response = await API.makeRequest<T>(`${BASE}${path}`, {
    ...QUIET,
    method: "POST",
    data
  });
  return response.data;
}

export default {
  async fetchApiKeys(): Promise<ApiKeysResponse> {
    return (await API.makeRequest<ApiKeysResponse>(BASE, QUIET)).data;
  },

  async createApiKey(_: unknown, params: ApiKeyParams): Promise<MintedApiKey> {
    return send<MintedApiKey>("", params);
  },

  async rollApiKey(
    _: unknown,
    { apiKeyId, credentialId, ...params }: RollApiKeyParams
  ): Promise<MintedApiKey> {
    return send<MintedApiKey>(
      `/${apiKeyId}/credentials/${credentialId}/roll`,
      params
    );
  },

  async revokeApiKey(
    _: unknown,
    { apiKeyId, credentialId }: { apiKeyId: string; credentialId: string }
  ): Promise<ApiKey> {
    return (
      await send<{ data: ApiKey }>(
        `/${apiKeyId}/credentials/${credentialId}/revoke`
      )
    ).data;
  }
};
